import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { MapPin } from 'lucide-react';
import type { FormData } from '../types';
import { loadingSteps, stepTitles } from '../lib/constants';
import PropertyTypeStep from './estimation/PropertyTypeStep';
import PropertyCharacteristicsStep from './estimation/PropertyCharacteristicsStep';
import BuildingCharacteristicsStep from './estimation/BuildingCharacteristicsStep';
import ApartmentConditionStep from './estimation/ApartmentConditionStep';
import BuildingConditionStep from './estimation/BuildingConditionStep';
import OptionalDetailsStep from './estimation/OptionalDetailsStep';
import ContactDetailsStep from './estimation/ContactDetailsStep';
import LoadingOverlay from './estimation/LoadingOverlay';
import ProgressBar from './estimation/ProgressBar';
import NavigationButtons from './estimation/NavigationButtons';
import HouseEstimationForm from './HouseEstimationForm';
import type { EstimationFormProps } from './estimation/types';

export default function EstimationForm({ initialAddress = '', visible = false, onClose }: EstimationFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [coordinates, setCoordinates] = useState<{longitude?: number; latitude?: number}>({});
  const [showPropertyTypeValidation, setShowPropertyTypeValidation] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    civilite: 'M',
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    adresse: initialAddress,
    type_propriete: '',
    surface: 0,
    nb_pieces: 0,
    nb_chambres: 0,
    nbr_salle_de_bain: 0,
    nbre_etages: 1,
    etage_bien: 0,
    annee_construction: '',
    ascenseur: false,
    cave: false,
    place_parking: false,
    garage: false,
    jardin: null,
    balcon_terasse: null,
    nature_chauffage: 'unknown',
    type_chauffage: 'unknown',
    etat_immeuble: '50',
    etat_facade: '50',
    etat_partie_communes: '50',
    etat_cuisine: '50',
    etat_salle_de_bain: '50',
    etat_sols: '50',
    etat_fenetres: '50',
    proprietaire: 'owner',
    exposition: 'unknown',
    type_cuisine: '',
    type_vitrage: '',
    type_parking: '',
    securite: [],
    showOptionalDetails: false,
    toilettes_separe: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialAddress) {
      setFormData(prev => ({ ...prev, adresse: initialAddress }));
      fetchCoordinates(initialAddress);
    }
  }, [initialAddress]);

  const fetchCoordinates = async (address: string) => {
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`);
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [longitude, latitude] = data.features[0].geometry.coordinates;
        setCoordinates({ longitude, latitude });
      }
    } catch (error) {
      console.error('Error fetching coordinates:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name === 'type_propriete' && value === 'Maison') {
      setFormData(prev => ({
        ...prev,
        type_propriete: value,
        surface: 0,
        nb_pieces: 0,
        nb_chambres: 0,
        nbr_salle_de_bain: 0,
        nbre_etages: 1,
        etage_bien: 0,
        annee_construction: '',
        ascenseur: false,
        cave: false,
        place_parking: false,
        garage: false,
        jardin: null,
        balcon_terasse: null,
        nature_chauffage: 'unknown',
        type_chauffage: 'unknown',
        etat_immeuble: '50',
        etat_facade: '50',
        etat_partie_communes: '50',
        etat_cuisine: '50',
        etat_salle_de_bain: '50',
        etat_sols: '50',
        etat_fenetres: '50'
      }));
      return;
    }

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      if (value === '' && (name === 'etage_bien' || name === 'annee_construction')) {
        setFormData(prev => ({ ...prev, [name]: '' }));
      } else {
        setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
      }
      if (errors[name]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  const handleNumberChange = (field: keyof FormData, increment: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: increment ? (prev[field] as number) + 1 : Math.max(0, (prev[field] as number) - 1)
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSliderChange = (field: keyof FormData) => (value: number) => {
    setFormData(prev => ({ ...prev, [field]: value.toString() }));
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};

    if (formData.surface <= 0) {
      newErrors.surface = 'La surface doit être supérieure à 0';
    }
    if (formData.nb_pieces <= 0) {
      newErrors.nb_pieces = 'Le nombre de pièces doit être supérieur à 0';
    }
    if (formData.nb_chambres <= 0) {
      newErrors.nb_chambres = 'Le nombre de chambres doit être supérieur à 0';
    }
    if (formData.nb_chambres > formData.nb_pieces) {
      newErrors.nb_chambres = 'Le nombre de chambres ne peut pas être supérieur au nombre de pièces';
    }
    if (formData.nbr_salle_de_bain <= 0) {
      newErrors.nbr_salle_de_bain = 'Le nombre de salles de bain doit être supérieur à 0';
    }
    if (formData.exposition === 'unknown') {
      newErrors.exposition = 'L\'exposition est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors: Record<string, string> = {};

    if (formData.nbre_etages <= 0) {
      newErrors.nbre_etages = 'Le nombre d\'étages doit être supérieur à 0';
    }
    
    if (formData.etage_bien < 0 || formData.etage_bien > 30) {
      newErrors.etage_bien = 'L\'étage doit être compris entre 0 et 30';
    }

    if (formData.etage_bien > formData.nbre_etages) {
      newErrors.etage_bien = 'L\'étage du bien ne peut pas être supérieur au nombre d\'étages de l\'immeuble';
    }

    if (!formData.annee_construction || formData.annee_construction < 1800) {
      newErrors.annee_construction = 'L\'année de construction doit être supérieure à 1800';
    }

    if (formData.nature_chauffage === 'unknown') {
      newErrors.nature_chauffage = 'La nature du chauffage est requise';
    }

    if (formData.type_chauffage === 'unknown') {
      newErrors.type_chauffage = 'Le type de chauffage est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const runLoadingAnimation = async () => {
    setIsSubmitting(true);
    for (let i = 0; i < loadingSteps.length; i++) {
      setLoadingStep(i);
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await runLoadingAnimation();
      
      // Fire and forget - don't await the response
      fetch('/api/submit-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, ...coordinates })
      }).catch(error => {
        console.error('Error in background submission:', error);
      });

      toast.success('Votre demande d\'estimation a été envoyée avec succès !');
      
      setFormData({
        civilite: 'M',
        prenom: '',
        nom: '',
        email: '',
        telephone: '',
        adresse: '',
        type_propriete: '',
        surface: 0,
        nb_pieces: 0,
        nb_chambres: 0,
        nbr_salle_de_bain: 0,
        nbre_etages: 1,
        etage_bien: 0,
        annee_construction: '',
        ascenseur: false,
        cave: false,
        place_parking: false,
        garage: false,
        jardin: null,
        balcon_terasse: null,
        nature_chauffage: 'unknown',
        type_chauffage: 'unknown',
        etat_immeuble: '50',
        etat_facade: '50',
        etat_partie_communes: '50',
        etat_cuisine: '50',
        etat_salle_de_bain: '50',
        etat_sols: '50',
        etat_fenetres: '50',
        proprietaire: 'owner',
        exposition: 'unknown',
        type_cuisine: '',
        type_vitrage: '',
        type_parking: '',
        securite: [],
        showOptionalDetails: false,
        toilettes_separe: false
      });
      setCurrentStep(1);
      setCoordinates({});

      if (onClose) {
        onClose();
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Une erreur est survenue lors de l\'envoi de votre demande');
    } finally {
      setIsSubmitting(false);
      setLoadingStep(0);
    }
  };

  const handlePropertyTypeValidation = () => {
    setShowPropertyTypeValidation(true);
    setTimeout(() => setShowPropertyTypeValidation(false), 3000);
  };

  const handleNext = () => {
    if (currentStep === 1 && !formData.type_propriete) {
      handlePropertyTypeValidation();
      return;
    }

    if (currentStep === 2) {
      if (validateStep2()) {
        setCurrentStep(prev => prev + 1);
      }
    } else if (currentStep === 3) {
      if (validateStep3()) {
        setCurrentStep(prev => prev + 1);
      }
    } else if (currentStep === 5 && !formData.showOptionalDetails) {
      setCurrentStep(7);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 7 && !formData.showOptionalDetails) {
      setCurrentStep(5);
    } else {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (currentStep > 1 && formData.type_propriete === 'Maison') {
    return (
      <HouseEstimationForm
        initialAddress={initialAddress}
        visible={visible}
        onClose={onClose}
        onBackToPropertyType={() => setCurrentStep(1)}
      />
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PropertyTypeStep 
            formData={formData} 
            handleInputChange={handleInputChange}
            handleNumberChange={handleNumberChange}
            handleSliderChange={handleSliderChange}
            errors={errors}
            showPropertyTypeValidation={showPropertyTypeValidation}
          />
        );
      case 2:
        return (
          <PropertyCharacteristicsStep 
            formData={formData} 
            handleInputChange={handleInputChange}
            handleNumberChange={handleNumberChange}
            handleSliderChange={handleSliderChange}
            errors={errors}
          />
        );
      case 3:
        return (
          <BuildingCharacteristicsStep 
            formData={formData} 
            handleInputChange={handleInputChange}
            handleNumberChange={handleNumberChange}
            handleSliderChange={handleSliderChange}
            errors={errors}
          />
        );
      case 4:
        return (
          <ApartmentConditionStep 
            formData={formData} 
            handleInputChange={handleInputChange}
            handleNumberChange={handleNumberChange}
            handleSliderChange={handleSliderChange}
            errors={errors}
          />
        );
      case 5:
        return (
          <BuildingConditionStep 
            formData={formData} 
            handleInputChange={handleInputChange}
            handleNumberChange={handleNumberChange}
            handleSliderChange={handleSliderChange}
            errors={errors}
          />
        );
      case 6:
        return (
          <OptionalDetailsStep 
            formData={formData} 
            handleInputChange={handleInputChange}
            handleNumberChange={handleNumberChange}
            handleSliderChange={handleSliderChange}
            errors={errors}
          />
        );
      case 7:
        return (
          <ContactDetailsStep 
            formData={formData} 
            handleInputChange={handleInputChange}
            handleNumberChange={handleNumberChange}
            handleSliderChange={handleSliderChange}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`max-w-4xl mx-auto px-4 py-12 ${visible ? 'animate-fade-in' : ''}`}>
      {isSubmitting && (
        <LoadingOverlay loadingStep={loadingStep} loadingSteps={loadingSteps} />
      )}

      <div className="bg-white rounded-2xl shadow-xl p-8">
        <ProgressBar 
          currentStep={currentStep} 
          totalSteps={7} 
          stepTitles={stepTitles} 
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {initialAddress && currentStep === 1 && (
            <div className="bg-white/80 backdrop-blur-xl p-8 rounded-xl shadow-sm mb-8">
              <div className="flex items-center justify-center space-x-4">
                <div className="bg-gradient-to-r from-[#2A4365]/10 via-[#4A5568]/10 to-[#C4A052]/10 p-3 rounded-full">
                  <MapPin className="h-10 w-10 text-[#2A4365]\" strokeWidth={2} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{initialAddress}</h3>
              </div>
            </div>
          )}

          {renderStepContent()}

          <NavigationButtons 
            currentStep={currentStep} 
            totalSteps={7} 
            onPrevious={handlePrevious} 
            onNext={handleNext}
            isSubmitting={isSubmitting}
          />
        </form>
      </div>
    </div>
  );
}